<script>
import {useRoute} from "vue-router";
import {useFundStore} from "@/javascript/stores/fund";
import Index from "@/javascript/views/components/dashboard/Index.vue";
import Show from "@/javascript/views/components/dashboard/Show.vue";
import NewFundModal from "@/javascript/views/clientDashboard/funds/NewFundModal.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import HeaderBar from "@/javascript/views/components/HeaderBar.vue";
import TabsComponent from "@/javascript/views/components/dashboard/Tabs.vue";
import DataCard from "@/javascript/views/components/overview/DataCard.vue";
import {computed} from "vue";
import {useOverviewStore} from "@/javascript/stores/overview";
import IntervalToggle from "@/javascript/views/components/overview/IntervalToggle.vue";

export default {
  name: "FundsShow",
  components: {IntervalToggle, DataCard, HeaderBar, Index, Show, NewFundModal, TabsComponent},
  setup() {
    const route = useRoute();
    const fundStore = useFundStore();
    const analyticEventStore = useAnalyticEventStore();
    const overviewStore = useOverviewStore();

    const dateRangeMessage = computed(() => {
      return overviewStore.dateRange || "Date range not set";
    });

    const topDonorsData = computed(() => {
      return {};
    });

    const handleIntervalChange = (newInterval) => {
      analyticEventStore.logEvent({
        action: `overview-change-interval`,
        metadata: {
          fromInterval: overviewStore.currentInterval,
          toInterval: newInterval
        }
      });
      overviewStore.currentInterval = newInterval;
      overviewStore.loadDataWithIntervals();
    };

    return {
      route,
      fundStore,
      analyticEventStore,
      dateRangeMessage,
      topDonorsData,
      handleIntervalChange,
    };
  },
  data() {
    return {
      // For the list (index) view:
      hasTabs: true,
      activeTab: "funds-tab",
      tabs: [
        {id: "funds-tab", label: "Funds"},
        // { id: "index-analytics", label: "Dashboard" },
      ],
      // For the show (detail) view:
      activeShowTab: "details",
      showTabs: [
        {id: "details", label: "Details"},
        // { id: "view-analytics", label: "Analytics" }
      ],
    };
  },
  computed: {
    fundId() {
      return this.route.params.id;
    },
    canCreate() {
      return this.fundStore.permissions && this.fundStore.permissions.create;
    }
  },
  watch: {
    "fundStore.organizationId"() {
      this.fundStore.loadData();
    }
  },
  mounted() {
    this.fundStore.loadData();
  },
  methods: {
    toggleNewModal() {
      this.fundStore.showNewModal = !this.fundStore.showNewModal;
      this.analyticEventStore.logEvent({action: "new-fund-modal-open"});
    }
  }
};
</script>

<template>
  <div>
    <!-- Detail (Show) view: if a fund is selected -->
    <div v-if="fundId && fundStore.currentRecord">
      <div class="mt-2 pt-2 w-full p-4 mx-auto">
        <div class="breakpoint-container relative rounded-xl overflow-hidden border border-gray-300 text-xs">
          <tabs-component
            v-model="activeShowTab"
            :tabs="showTabs"
          >
            <!-- Details tab: renders the Show component -->
            <template #details>
              <show
                :model-store="fundStore"
                editable
              />
            </template>
            <!-- Analytics tab: placeholder content -->
            <template #view-analytics>
              <div class="p-4">
                <!-- Interval Toggle component -->
                <div class="flex justify-end">
                  <interval-toggle @interval-change="handleIntervalChange" />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <data-card
                    id="overview-top-donors"
                    title="Top Donors"
                    :additional-text="'Date range not set'"
                    title-icon="award"
                    icon-color="#10be5a"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                  <data-card
                    id="overview-gift-frequency"
                    title="Gift Frequency"
                    :additional-text="'Date range not set'"
                    title-icon="chart-line"
                    icon-color="#007bff"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                  <data-card
                    id="overview-gift-type"
                    title="Gift Type"
                    :additional-text="'Date range not set'"
                    title-icon="chart-pie"
                    icon-color="#ff5722"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                  <data-card
                    id="overview-top-campaigns"
                    title="Top Campaigns"
                    :additional-text="'Date range not set'"
                    title-icon="award"
                    icon-color="#28a745"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                </div>
              </div>
            </template>
          </tabs-component>
        </div>
      </div>
    </div>
    <!-- Otherwise: index view (list view) -->
    <div v-else>
      <!-- Header Bar with title and New Fund button -->
      <div class="px-4">
        <header-bar title="Funds">
          <template #buttons>
            <button
              v-if="canCreate"
              :key="fundStore.permissions"
              class="btn-outline"
              @click="toggleNewModal"
            >
              New Fund
            </button>
          </template>
        </header-bar>
      </div>
      <!-- Index view wrapped in TabsComponent -->
      <div
        v-if="hasTabs"
        class="mt-2 pt-2 w-full p-4 mx-auto"
      >
        <div class="breakpoint-container relative rounded-xl overflow-hidden border border-gray-300 text-xs">
          <tabs-component
            v-model="activeTab"
            :tabs="tabs"
          >
            <template #funds-tab>
              <index :model-store="fundStore" />
            </template>
            <template #index-analytics>
              <div class="p-4">
                <!-- Interval Toggle component -->
                <div class="flex justify-end">
                  <interval-toggle @interval-change="handleIntervalChange" />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <data-card
                    id="overview-top-donors"
                    title="Top Donors"
                    :additional-text="'Date range not set'"
                    title-icon="award"
                    icon-color="#10be5a"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                  <data-card
                    id="overview-gift-frequency"
                    title="Gift Frequency"
                    :additional-text="'Date range not set'"
                    title-icon="chart-line"
                    icon-color="#007bff"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                  <data-card
                    id="overview-gift-type"
                    title="Gift Type"
                    :additional-text="'Date range not set'"
                    title-icon="chart-pie"
                    icon-color="#ff5722"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                  <data-card
                    id="overview-top-campaigns"
                    title="Top Campaigns"
                    :additional-text="'Date range not set'"
                    title-icon="award"
                    icon-color="#28a745"
                    loaded
                  >
                    <div class="h-32 flex items-center justify-center">
                      Blank Graph
                    </div>
                  </data-card>
                </div>
              </div>
            </template>
          </tabs-component>
        </div>
      </div>
      <div
        v-else
        class="breakpoint-container mt-4"
      >
        <index :model-store="fundStore" />
      </div>
      <!-- New Fund Modal -->
      <new-fund-modal v-if="fundStore.showNewModal" />
    </div>
  </div>
</template>
